import React from "react";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle, pct } from "./PersonaUi";
import { DIGEST_DIVERGE, DIGEST_EXITS, DIGEST_NEW, EVIDENCE, SEASON } from "./personaData";

const Table = ({ cols, children, testid }) => (
  <table className="w-full text-xs" data-testid={testid}>
    <thead className="border-b border-border text-muted-foreground"><tr>{cols.map((c, i) => <th key={c} className={`py-1.5 px-2 ${i === 0 ? "text-left" : "text-right"}`}>{c}</th>)}</tr></thead>
    <tbody>{children}</tbody>
  </table>
);

export default function HfSeasonDigest() {
  const prog = SEASON.processed / SEASON.expected;
  return (
    <div className="space-y-4" data-testid="persona-hf-digest">
      <PainBanner testid="persona-hf-digest-pain" persona="HF ANALYST · FILING-SEASON DIGEST"
        pain="13F season dumps ~5,000 filings in 45 days — what changed in MY coverage, and what should I look at first?"
        answer="coverage-filtered changes, ranked by evidence" />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Headline testid="hf-digest-headline">5 new high-conviction cohorts · 3 names with ≥2 skilled exits · 3 street/smart-money splits</Headline>
          <div className="text-sm text-muted-foreground mt-1">{SEASON.sector} coverage ({SEASON.coverage} names) · {SEASON.quarter} · deadline {SEASON.deadline}</div>
        </div>
        <div className="w-72" data-testid="hf-digest-season-progress">
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1"><span>Filings processed</span><span className="font-mono">{SEASON.processed.toLocaleString()} / ~{SEASON.expected.toLocaleString()}</span></div>
          <div className="h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-sky-500" style={{ width: `${prog * 100}%` }} /></div>
          <div className="text-[10px] text-muted-foreground mt-1">{Math.round(prog * 100)}% in · cohorts firm up after the deadline</div>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="hf-kpi-cohorts" label="Names with a new cohort" value="131" sub={`of ${SEASON.coverage} covered · 38 score S9–S10`} />
        <Kpi testid="hf-kpi-elite" label="First-time elite entries" value="11" tone="text-emerald-400" sub="top-decile sector pickers" />
        <Kpi testid="hf-kpi-exits" label="Skilled exits" value="17" tone="text-amber-400" sub="3 names with ≥2" />
        <Kpi testid="hf-kpi-crowding" label="Crowding up" value="9" sub="moved into top decile" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card><CardContent className="pt-4">
          <SectionTitle right={<Pill tone="good">LONG IDEAS</Pill>}>New high-conviction cohorts</SectionTitle>
          <Table testid="hf-digest-new-table" cols={["Ticker", "Band", "New buyers", "Elite names", "Pros' gain", "V3 exp. 6m"]}>
            {DIGEST_NEW.map((r) => (
              <tr key={r.sym} className="border-b border-border/40 hover:bg-muted/30" data-testid={`hf-digest-new-${r.sym}`}>
                <td className="py-2 px-2"><span className="font-mono font-bold">{r.sym}</span><div className="text-[10px] text-muted-foreground">{r.note}</div></td>
                <td className="py-2 px-2 text-right"><Pill tone="info">{r.band}</Pill></td>
                <td className="py-2 px-2 text-right font-mono">{r.buyers}</td>
                <td className="py-2 px-2 text-right text-[11px]">{r.elite}</td>
                <td className="py-2 px-2 text-right font-mono text-emerald-400">{pct(r.bp)}</td>
                <td className="py-2 px-2 text-right font-mono">{pct(r.exp)}</td>
              </tr>
            ))}
          </Table>
          <div className="text-[10px] text-muted-foreground mt-2">{EVIDENCE.v3}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <SectionTitle right={<Pill tone="alert">REVIEW / SHORT IDEAS</Pill>}>Skilled holders leaving</SectionTitle>
          <Table testid="hf-digest-exit-table" cols={["Ticker", "Skilled exits", "Who", "Pros' gain", "Crowding pct"]}>
            {DIGEST_EXITS.map((r) => (
              <tr key={r.sym} className="border-b border-border/40 hover:bg-muted/30" data-testid={`hf-digest-exit-${r.sym}`}>
                <td className="py-2 px-2 font-mono font-bold">{r.sym}</td>
                <td className="py-2 px-2 text-right font-mono text-amber-400">{r.n}</td>
                <td className="py-2 px-2 text-right text-[11px]">{r.who}</td>
                <td className="py-2 px-2 text-right font-mono text-red-400">{pct(r.conf)}</td>
                <td className="py-2 px-2 text-right font-mono">{r.crowd}th</td>
              </tr>
            ))}
          </Table>
          <div className="text-[10px] text-muted-foreground mt-2">{EVIDENCE.exit}</div>
        </CardContent></Card>
      </div>
      <Card><CardContent className="pt-4">
        <SectionTitle right={<Pill tone="violet">VARIANT PERCEPTION</Pill>}>Street vs smart money — where they disagree</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="hf-digest-divergence">
          {DIGEST_DIVERGE.map((d) => (
            <div key={d.sym} className="rounded-md border border-border/70 p-3" data-testid={`hf-digest-diverge-${d.sym}`}>
              <div className="flex items-center justify-between"><span className="font-mono font-bold">{d.sym}</span><span className="text-[11px] text-violet-300">{d.read}</span></div>
              <div className="text-[11px] mt-2"><span className="text-muted-foreground">Street target:</span> {d.street}</div>
              <div className="text-[11px]"><span className="text-muted-foreground">Smart money:</span> {d.smart}</div>
            </div>
          ))}
        </div>
      </CardContent></Card>
    </div>
  );
}
